// umlaut-check: skip — technische Schlüssel der Bestandskennzahlen (JSON-Schnittstelle).
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {usePage} from "@inertiajs/react";
import axios from "axios";
import {Alert, Button, Container, Form} from "react-bootstrap";
import {createColumnHelper} from "@tanstack/react-table";
import {mdiInformationOutline} from "@mdi/js";
import DataGrid from "../../Components/DataGrid/DataGrid.jsx";
import HelpTooltip from "../../Components/HelpTooltip.jsx";
import StatusTimestampControl from "../../Components/StatusTimestampControl.jsx";
import ArticleHistoryModal from "../../Components/ArticleHistoryModal.jsx";
import ArticleShopLinks from "../../Components/ArticleShopLinks.jsx";
import ReservationModal from "../../Components/Stock/ReservationModal.jsx";
import DataFreshnessButton from "../../Components/Stock/DataFreshnessButton.jsx";
import BestandTooltip from "../../Components/Stock/BestandTooltip.jsx";

const columnHelper = createColumnHelper();

const LAGER = {
    snv: {id: "8034", name: "SNV Weilheim"},
    lahr: {id: "3827", name: "Lahr Hauptlager"},
};

/**
 * Bestandszahl eines Lagers mit dem gemeinsamen Erklär-Tooltip
 * (einheitliche Bestandsregel, Auftrag #315): Lagerbestand, reserviert,
 * verfügbar und — beim Öffnen nachgeladen — die reservierenden Lieferscheine
 * und Aufträge.
 */
function LagerZahl({row, lager, feld, wert}) {
    const {id, name} = LAGER[lager];
    const reserviert = row[`${lager}_reserved`];
    const verfuegbar = row[`${lager}_available`];
    const stand = row.hart_reserviert_synced_at
        ? `Reservierungen: Stand ${formatTimestamp(row.hart_reserviert_synced_at)} (stündlicher Abgleich mit Weclapp).`
        : "Die Reservierungen wurden noch nicht abgeglichen.";

    return <BestandTooltip
        werte={{
            lagerbestand: row[lager] ?? 0,
            reserviertFremd: reserviert === null || reserviert === undefined ? undefined : reserviert,
            verfuegbar: verfuegbar === null || verfuegbar === undefined ? undefined : verfuegbar,
        }}
        lagerName={name}
        quelle={`${stand}${row.synced_at ? ` Lagerbestand: Stand ${formatTimestamp(row.synced_at)}.` : ""}`}
        haelterUrl="/stock/overview/hard-reservations"
        haelterParams={{artikelnummer: row.artikelnummer, warehouse: id}}
    >
        {feld === "lagerbestand" && verfuegbar !== null && verfuegbar !== undefined
            ? <span tabIndex={0} className="d-inline-block text-nowrap" data-feld={feld}>
                <small className="text-muted me-1" data-teil="physisch">({formatQuantity(row[lager] ?? 0)})</small>
                <span data-teil="verfuegbar">{formatQuantity(verfuegbar)}</span>
            </span>
            : <span tabIndex={0} className="d-inline-block" data-feld={feld}>{formatQuantity(wert)}</span>}
    </BestandTooltip>;
}

// Die technischen Werte bleiben "in"/"out". "In Zulauf" ist die verbindliche
// Bezeichnung für den Zustand, der früher "nicht auf Lager" hieß - sie gilt
// gleichlautend im Änderungslog und im täglichen Bestandsbericht per E-Mail.
const SOLL_STATUS_CONFIG = {
    in: {label: "Auf Lager", variant: "success"},
    out: {label: "In Zulauf", variant: "warning"},
    no_stock: {label: "ohne Bestand", variant: "secondary"},
};

const SHOP_STATUS_CONFIG = {
    in: {label: "Auf Lager", variant: "success"},
    out: {label: "In Zulauf", variant: "warning"},
};

const SHOP_STATUS_FILTER_OPTIONS = [
    {value: "", label: "Alle"},
    {value: "in", label: "Auf Lager"},
    {value: "out", label: "In Zulauf"},
    {value: "__none__", label: "– (unbekannt)"},
];

const SHOP_ACTIVE_CONFIG = {
    active: {label: "Aktiviert", variant: "primary", marker: "●"},
    inactive: {label: "Deaktiviert", variant: "secondary", marker: "○"},
    xxx_disabled: {label: "Durch XXX deaktiviert", variant: "dark", marker: "×"},
};

const DEFAULT_ADDITIONAL_FILTERS = {only_shop_articles: true, shop_scope: ""};

const B2C_SHOP_ACTIVE_FILTER_OPTIONS = [
    {value: "", label: "Alle"},
    {value: "active", label: "Aktiviert"},
    {value: "inactive", label: "Deaktiviert"},
    {value: "__none__", label: "– (unbekannt)"},
];

const B2B_SHOP_ACTIVE_FILTER_OPTIONS = [
    {value: "", label: "Alle"},
    {value: "active", label: "Aktiviert"},
    {value: "inactive", label: "Deaktiviert"},
    {value: "xxx_disabled", label: "Durch XXX deaktiviert"},
    {value: "__none__", label: "– (unbekannt)"},
];

function renderShopStatusBadge(value) {
    if (!value) {
        return <span className="text-muted">–</span>;
    }
    const cfg = SHOP_STATUS_CONFIG[value] || {label: value, variant: "light"};
    return <span className={`badge bg-${cfg.variant}`}>{cfg.label}</span>;
}

function renderShopActiveBadge(value) {
    const cfg = SHOP_ACTIVE_CONFIG[value];
    if (!cfg) {
        return <span className="text-muted">–</span>;
    }
    return <span className={`badge rounded-pill bg-${cfg.variant} d-inline-flex align-items-center gap-1`}>
        <span aria-hidden="true">{cfg.marker}</span>{cfg.label}
    </span>;
}

function formatQuantity(value) {
    if (value === null || value === undefined) {
        return "–";
    }
    return new Intl.NumberFormat("de-DE").format(value);
}

function formatTimestamp(value) {
    if (!value) {
        return "–";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "–";
    }
    return date.toLocaleString("de-DE", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
    });
}

function formatPrice(value, currency) {
    if (value === null || value === undefined) return "–";
    return new Intl.NumberFormat("de-DE", {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(value) + (currency ? ` ${currency}` : "");
}

/**
 * Liest Vorfilter aus der URL, damit z. B. der Link von der
 * B2B-Lagerverfügbarkeit ("?ueberwacht=1") die Tabelle direkt gefiltert öffnet.
 */
function parseInitialFilters() {
    if (typeof window === "undefined") {
        return [];
    }

    const params = new URLSearchParams(window.location.search);
    const filters = [];

    if (params.get("ueberwacht") === "1") {
        filters.push({id: "ueberwacht", value: "ja"});
    }

    const sollStatus = params.get("soll_status");
    if (sollStatus) {
        filters.push({id: "soll_status", value: sollStatus});
    }

    return filters;
}

/**
 * Die Navigation soll immer mit der Arbeitsansicht „Lager“ starten. Sobald
 * die URL aber einen Zustand trägt (Ansicht oder Vorfilter), ist sie die
 * Quelle der Wahrheit: ein Reload darf diesen Zustand nicht überschreiben.
 */
function initialViewId(views) {
    const lager = views.find((view) => view.slug === "lager");
    if (typeof window === "undefined") return lager?.id ?? null;

    const params = new URLSearchParams(window.location.search);
    const requestedSlug = params.get("view");
    if (requestedSlug) return views.find((view) => view.slug === requestedSlug)?.id ?? null;

    return params.size === 0 ? lager?.id ?? null : null;
}

export default function Overview() {
    const {
        threshold,
        warehouse_labels: warehouseLabels,
        stock_data_warning: stockDataWarning,
        warengruppen,
        price_columns: priceColumns = [],
        advanced_search_fields: advancedSearchFields = [],
        saved_searches: savedSearches = [],
        views: suppliedViews = [],
        user_roles: userRoles = [],
        weclappBaseUrl = "",
    } = usePage().props;

    // Die eigentlichen Artikel-Zeilen lädt DataGrid serverseitig selbst nach
    // (Seite, Filter, Sortierung). Für den Live-Ersatz einer einzelnen Zeile
    // nach "Jetzt aktualisieren" (StatusTimestampControl) greift diese Seite
    // per Ref auf DataGrids interne Zeilen-Verwaltung durch.
    const gridRef = useRef(null);
    const viewWasDraggedRef = useRef(false);

    const handleArticleRefreshed = useCallback((updatedArticle) => {
        if (!updatedArticle?.artikelnummer) {
            return;
        }
        gridRef.current?.replaceRow(updatedArticle.artikelnummer, updatedArticle);
    }, []);

    // EIN gemeinsames Verlaufsfenster für alle Zeilen (statt eines Modals je
    // Zeile) - geöffnet wird es über das Uhrensymbol in der
    // Artikelnummer-Spalte, das den Artikel dieser Zeile setzt.
    const [historyArtikelnummer, setHistoryArtikelnummer] = useState(null);
    const [reservationArtikelnummer, setReservationArtikelnummer] = useState(null);
    const [views, setViews] = useState(suppliedViews);
    const [activeViewId, setActiveViewId] = useState(() => initialViewId(suppliedViews));
    const [gridState, setGridState] = useState(null);
    const [viewName, setViewName] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [draftViewIds, setDraftViewIds] = useState([]);
    const [priceComparison, setPriceComparison] = useState(null);
    const [stockWarning, setStockWarning] = useState(stockDataWarning);
    const [refreshingStock, setRefreshingStock] = useState(false);
    const [stockRefreshError, setStockRefreshError] = useState(null);
    const isAdmin = userRoles.includes("admin");

    const initialColumnFilters = useMemo(() => parseInitialFilters(), []);

    const refreshStockData = async () => {
        setRefreshingStock(true);
        setStockRefreshError(null);
        try {
            const {data} = await axios.post("/stock/overview/refresh-stock-data");
            setStockWarning(data?.stale ? {last_successful_at: data.last_successful_at, stale: true} : null);
            gridRef.current?.applyState(gridRef.current?.getState());
        } catch (error) {
            setStockRefreshError(error.response?.data?.message || "Bestandsabruf fehlgeschlagen.");
        } finally {
            setRefreshingStock(false);
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor("hex_color", {
                id: "hex_color",
                header: "Farbe",
                enableSorting: false,
                enableColumnFilter: false,
                meta: {
                    width: 64,
                    help: "Farbmuster des Artikels. HEX-Farbwert aus dem B2C-Shop; bei Effektfarben (Metallic/Verlauf) stattdessen ein Vorschaubild. Leer bei Artikeln ohne hinterlegte Farbe (z. B. Zubehör, Sets).",
                },
                cell: (info) => {
                    const hexColor = info.getValue();
                    const colorImageUrl = info.row.original.color_image_url;

                    if (hexColor) {
                        return (
                            <span className="d-inline-flex flex-column align-items-start" title={hexColor}>
                                <span
                                    className="d-inline-block rounded-1"
                                    style={{
                                        width: "48px",
                                        height: "24px",
                                        maxWidth: "100%",
                                        border: "1px solid rgba(0,0,0,.15)",
                                        backgroundColor: hexColor,
                                    }}
                                />
                                {/* Der Farbwert direkt unter dem Muster - klein
                                    gehalten, damit die Spalte schmal bleibt. */}
                                <span
                                    className="text-muted font-monospace"
                                    style={{fontSize: "0.65rem", lineHeight: 1.3, letterSpacing: "-0.01em"}}
                                >
                                    {hexColor.toUpperCase()}
                                </span>
                            </span>
                        );
                    }

                    if (colorImageUrl) {
                        return (
                            <span
                                className="d-inline-block rounded-1"
                                style={{
                                    width: "48px",
                                    height: "24px",
                                    maxWidth: "100%",
                                    border: "1px solid rgba(0,0,0,.15)",
                                    backgroundImage: `url(${colorImageUrl})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            />
                        );
                    }

                    return null;
                },
            }),
            columnHelper.accessor("artikelnummer", {
                id: "artikelnummer",
                header: "Artikelnummer",
                cell: (info) => (
                    <ArticleShopLinks
                        sku={info.getValue()}
                        b2cDeId={info.row.original.b2c_de_id}
                        b2bStatus={info.row.original.b2b_status}
                        weclappId={info.row.original.weclapp_id}
                        onShowHistory={setHistoryArtikelnummer}
                    />
                ),
                meta: {
                    width: 170,
                    filterVariant: "text",
                    help: "Die Artikelnummer (SKU, SNV-/Schneider-Nummer). Sie verknüpft Weclapp, den B2B- und den B2C-Shop. Darunter Schaltflächen, in welchem System der Artikel geöffnet werden kann (B2C, B2B, Weclapp), sowie ein Uhrensymbol für den Änderungsverlauf dieses Artikels.",
                    // In der Kartendarstellung (< 992px) die Kartenüberschrift -
                    // die erste sichtbare Spalte wäre sonst das Farbmuster.
                    cardTitle: true,
                },
            }),
            columnHelper.accessor("bezeichnung", {
                id: "bezeichnung",
                header: "Bezeichnung",
                cell: (info) => info.getValue() || "–",
                meta: {
                    width: 280,
                    filterVariant: "text",
                    help: "Artikelname, wie er in Weclapp hinterlegt ist. Kann von der Bezeichnung im Shop abweichen – maßgeblich für die Zuordnung ist immer die Artikelnummer, nicht der Name.",
                },
            }),
            columnHelper.accessor("warengruppe", {
                id: "warengruppe",
                header: "Warengruppe",
                cell: (info) => info.getValue() || "–",
                meta: {
                    width: 190,
                    pickerLabel: "Warengruppe",
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        ...(warengruppen || []).map((w) => ({value: w, label: w})),
                    ],
                    help: "Produktkategorie/-serie aus Weclapp (z. B. „PREMIUM 400ml Spray\"). Über den Filter-Schalter oben lässt sich die Tabelle nach dieser Spalte gruppieren.",
                },
            }),
            columnHelper.accessor("snv", {
                id: "snv",
                header: warehouseLabels?.["8034"] || "SNV Weilheim",
                cell: (info) => <LagerZahl row={info.row.original} lager="snv" feld="lagerbestand" wert={info.getValue()}/>,
                meta: {
                    width: 160,
                    pickerLabel: "SNV Weilheim",
                    filterVariant: "number",
                    help: "Große Zahl = verfügbar (Lagerbestand minus reserviert), davor klein in Klammern der physische Lagerbestand im Lager SNV Weilheim, laut letztem Abgleich mit Weclapp. Sortieren und Filtern dieser Spalte richten sich nach dem physischen Bestand; nach „verfügbar“ sortiert und filtert die gleichnamige Spalte. Der physische Bestand ist der maßgebliche Bestand für die Shops: Aus ihm berechnet die Automatik, ob der Artikel im B2B-Shop auf „Auf Lager\" oder „In Zulauf\" steht – die Shops bleiben bewusst beim physischen Bestand. Aufträge reservieren Bestand: was davon für Aufträge zurückgelegt ist, zeigt die Spalte „SNV Weilheim reserviert\", was noch verfügbar ist, die Spalte „SNV Weilheim verfügbar\".",
                },
            }),
            columnHelper.accessor("snv_reserved", {
                id: "snv_reserved",
                header: "SNV reserviert",
                cell: (info) => <LagerZahl row={info.row.original} lager="snv" feld="reserviert" wert={info.getValue()}/>,
                meta: {
                    width: 120,
                    pickerLabel: "SNV Weilheim reserviert",
                    filterVariant: "number",
                    help: "Reserviert: die Menge im Lager SNV Weilheim, die Aufträge oder Lieferscheine bereits per Entnahme zurückgelegt haben. Sie liegt noch im Lager, steht aber anderen Aufträgen nicht mehr zur Verfügung. Stand des stündlichen Abgleichs mit Weclapp; der Tooltip an der Zahl nennt die Lieferscheine und Aufträge dahinter.",
                },
            }),
            columnHelper.accessor("snv_available", {
                id: "snv_available",
                header: "SNV verfügbar",
                cell: (info) => <LagerZahl row={info.row.original} lager="snv" feld="verfuegbar" wert={info.getValue()}/>,
                meta: {
                    width: 120,
                    pickerLabel: "SNV Weilheim verfügbar",
                    filterVariant: "number",
                    help: "Verfügbar = Lagerbestand in SNV Weilheim minus die dort reservierte Menge. Offene, noch nicht reservierte Mengen werden nicht abgezogen, sie stehen nur grau in der Spalte „Offen, nicht reserviert\".",
                },
            }),
            columnHelper.accessor("snv_zero_since", {
                id: "snv_zero_since",
                header: "SNV Bestand 0 seit",
                cell: (info) => info.row.original.snv === 0 ? formatTimestamp(info.getValue()) : "–",
                meta: {
                    width: 145,
                    pickerLabel: "SNV Bestand 0 seit",
                    help: "Seit wann der Artikel in SNV Weilheim durchgehend auf 0 steht – also wie lange er schon nicht mehr lieferbar ist. Ein Strich heißt nicht „vorrätig\": Er steht auch dann, wenn für diesen Artikel in Weilheim noch nie ein Bestand geführt wurde. Den tatsächlichen Bestand zeigt die Spalte links daneben.",
                },
            }),
            columnHelper.accessor("lahr", {
                id: "lahr",
                header: warehouseLabels?.["3827"] || "Lahr Hauptlager",
                cell: (info) => <LagerZahl row={info.row.original} lager="lahr" feld="lagerbestand" wert={info.getValue()}/>,
                meta: {
                    width: 170,
                    pickerLabel: "Lahr Hauptlager",
                    filterVariant: "number",
                    help: "Große Zahl = verfügbar (Lagerbestand minus reserviert), davor klein in Klammern der physische Lagerbestand im Lager Lahr Hauptlager, laut letztem Abgleich mit Weclapp. Sortieren und Filtern dieser Spalte richten sich nach dem physischen Bestand; nach „verfügbar“ sortiert und filtert die gleichnamige Spalte. Nur zur Information: Dieser Bestand steuert nichts – weder den B2B- noch den B2C-Shop. Die Zahlen aus Lahr sind derzeit nicht überall verlässlich gepflegt, im Zweifel gilt SNV Weilheim. Was davon reserviert und noch verfügbar ist, zeigen die beiden Spalten rechts daneben.",
                },
            }),
            columnHelper.accessor("lahr_reserved", {
                id: "lahr_reserved",
                header: "Lahr reserviert",
                cell: (info) => <LagerZahl row={info.row.original} lager="lahr" feld="reserviert" wert={info.getValue()}/>,
                meta: {
                    width: 120,
                    pickerLabel: "Lahr Hauptlager reserviert",
                    filterVariant: "number",
                    help: "Reserviert: die Menge im Lager Lahr Hauptlager, die Aufträge oder Lieferscheine bereits per Entnahme zurückgelegt haben. Sie liegt noch im Lager, steht aber anderen Aufträgen nicht mehr zur Verfügung. Stand des stündlichen Abgleichs mit Weclapp; der Tooltip an der Zahl nennt die Lieferscheine und Aufträge dahinter.",
                },
            }),
            columnHelper.accessor("lahr_available", {
                id: "lahr_available",
                header: "Lahr verfügbar",
                cell: (info) => <LagerZahl row={info.row.original} lager="lahr" feld="verfuegbar" wert={info.getValue()}/>,
                meta: {
                    width: 120,
                    pickerLabel: "Lahr Hauptlager verfügbar",
                    filterVariant: "number",
                    help: "Verfügbar = Lagerbestand in Lahr Hauptlager minus die dort reservierte Menge. Offene, noch nicht reservierte Mengen werden nicht abgezogen, sie stehen nur grau in der Spalte „Offen, nicht reserviert\".",
                },
            }),
            columnHelper.accessor("lahr_zero_since", {
                id: "lahr_zero_since",
                header: "Lahr Bestand 0 seit",
                cell: (info) => info.row.original.lahr === 0 ? formatTimestamp(info.getValue()) : "–",
                meta: {
                    width: 145,
                    pickerLabel: "Lahr Bestand 0 seit",
                    help: "Seit wann der Artikel in Lahr Hauptlager durchgehend auf 0 steht. Ein Strich heißt nicht „vorrätig\": Er steht auch dann, wenn für diesen Artikel in Lahr noch nie ein Bestand geführt wurde.",
                },
            }),
            columnHelper.accessor("ueberwacht", {
                id: "ueberwacht",
                header: "Für B2B-Shop überwacht",
                cell: (info) => (
                    <span className={`badge ${info.getValue() ? "bg-info" : "bg-light text-dark"}`}>
                        {info.getValue() ? "Ja" : "Nein"}
                    </span>
                ),
                meta: {
                    width: 120,
                    pickerLabel: "Für B2B-Shop überwacht",
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        {value: "ja", label: "Ja"},
                        {value: "nein", label: "Nein"},
                    ],
                    help: "Ob die Automatik diesen Artikel im B2B-Shop selbstständig zwischen „Auf Lager\" und „In Zulauf\" umschaltet. Bei „Nein\" bleibt der Shop-Eintrag unverändert stehen, bis jemand ihn von Hand ändert – der Bestand kann dann also veraltet sein.",
                },
            }),
            columnHelper.accessor("b2c_active", {
                id: "b2c_active",
                header: "B2C-Shop-Status",
                cell: (info) => renderShopActiveBadge(info.getValue()),
                meta: {
                    width: 150,
                    pickerLabel: "Aktivierung",
                    filterVariant: "select",
                    selectOptions: B2C_SHOP_ACTIVE_FILTER_OPTIONS,
                    help: <>
                        <div className="mb-1">Ob der Artikel im B2C-Shop (shop.molotow.com) überhaupt sichtbar und bestellbar ist – also die Freischaltung, nicht die Lagerlage.</div>
                        <div><strong>Aktiviert:</strong> im Shop vorhanden und freigeschaltet.</div>
                        <div><strong>Deaktiviert:</strong> im Shop vorhanden, aber abgeschaltet – Kunden sehen ihn nicht.</div>
                        <div><strong>–:</strong> im Shop gar nicht gefunden.</div>
                        <div className="mt-1">Nicht verwechseln mit der Nachbarspalte „B2C-Shop-Lagerstatus": Die sagt, ob ein sichtbarer Artikel als vorrätig oder als in Zulauf ausgewiesen wird. Ein Artikel kann freigeschaltet und trotzdem „In Zulauf" sein. Maßgeblich ist die deutsche Sprachfassung des Shops.</div>
                    </>,
                },
            }),
            columnHelper.accessor("b2c_status", {
                id: "b2c_status",
                header: "B2C-Shop-Lagerstatus",
                cell: (info) => (
                    <span className="d-inline-flex align-items-center">
                        {renderShopStatusBadge(info.getValue())}
                        <StatusTimestampControl
                            timestamp={info.row.original.b2c_status_synced_at}
                            label="Stand des B2C-Shops"
                            artikelnummer={info.row.original.artikelnummer}
                            onRefreshed={handleArticleRefreshed}
                        />
                    </span>
                ),
                meta: {
                    width: 160,
                    pickerLabel: "Verfügbarkeit",
                    filterVariant: "select",
                    selectOptions: SHOP_STATUS_FILTER_OPTIONS,
                    help: "Was der Kunde im B2C-Shop (shop.molotow.com) über die Lieferbarkeit liest: „Auf Lager\" oder „In Zulauf\". Das ist der Zustand, wie er dort tatsächlich eingestellt ist – nicht der von der Automatik berechnete Wunschzustand (das ist die Spalte „Sollstatus\") und nicht die Freischaltung (das ist die Spalte links daneben). „–\" = der Artikel wurde im B2C-Shop nicht gefunden.",
                },
            }),
            columnHelper.accessor("b2b_active", {
                id: "b2b_active",
                header: "B2B-Shop-Status",
                cell: (info) => renderShopActiveBadge(info.getValue()),
                meta: {
                    width: 185,
                    pickerLabel: "Aktivierung",
                    filterVariant: "select",
                    selectOptions: B2B_SHOP_ACTIVE_FILTER_OPTIONS,
                    help: <>
                        <div className="mb-1">Ob der Artikel im B2B-Shop (b2b.molotow.com) sichtbar und bestellbar ist – also die Freischaltung, nicht die Lagerlage.</div>
                        <div><strong>Aktiviert:</strong> im B2B-Shop vorhanden und freigeschaltet, regulär bestellbar.</div>
                        <div><strong>Deaktiviert:</strong> im Shop abgeschaltet und im Katalog nicht bestellbar. Über den CSV-Import ist der Artikel aber weiterhin bestellbar.</div>
                        <div><strong>Durch XXX deaktiviert:</strong> Der Artikelnummer wurde im Shop ein <code>XXX_</code> vorangestellt. Dadurch findet auch der CSV-Import den Artikel nicht mehr; er ist auf allen Wegen gesperrt.</div>
                        <div><strong>–:</strong> im B2B-Shop nicht gefunden, auch nicht unter dem Präfix.</div>
                        <div className="mt-1">Nicht verwechseln mit der Nachbarspalte „B2B-Shop-Lagerstatus": Die sagt, ob ein sichtbarer Artikel als vorrätig oder als in Zulauf ausgewiesen wird.</div>
                    </>,
                },
            }),
            columnHelper.accessor("b2b_status", {
                id: "b2b_status",
                header: "B2B-Shop-Lagerstatus",
                cell: (info) => (
                    <span className="d-inline-flex align-items-center">
                        {renderShopStatusBadge(info.getValue())}
                        <StatusTimestampControl
                            timestamp={info.row.original.b2b_status_synced_at}
                            label="Stand des B2B-Shops"
                            artikelnummer={info.row.original.artikelnummer}
                            onRefreshed={handleArticleRefreshed}
                        />
                    </span>
                ),
                meta: {
                    width: 160,
                    pickerLabel: "Verfügbarkeit",
                    filterVariant: "select",
                    selectOptions: SHOP_STATUS_FILTER_OPTIONS,
                    help: "Was der Kunde im B2B-Shop (b2b.molotow.com) über die Lieferbarkeit liest: „Auf Lager\" oder „In Zulauf\". Das ist der Zustand, wie er dort tatsächlich eingestellt ist – nicht der von der Automatik berechnete Wunschzustand (das ist die Spalte „Sollstatus B2B-Shop\") und nicht die Freischaltung (das ist die Spalte links daneben). Weicht dieser Wert vom Sollstatus ab, hat der stündliche Abgleich noch nicht gegriffen. „–\" = der Artikel wurde im B2B-Shop nicht gefunden.",
                },
            }),
            columnHelper.accessor("soll_status", {
                id: "soll_status",
                header: "Sollstatus B2B-Shop",
                cell: (info) => {
                    const value = info.getValue();
                    const badge = !value ? (
                        <span className="text-muted">–</span>
                    ) : (
                        (() => {
                            const cfg = SOLL_STATUS_CONFIG[value] || {label: value, variant: "light"};
                            return <span className={`badge bg-${cfg.variant}`}>{cfg.label}</span>;
                        })()
                    );
                    return (
                        <span className="d-inline-flex align-items-center">
                            {badge}
                            <StatusTimestampControl
                                timestamp={info.row.original.synced_at}
                                label="Stand der Weclapp-Bestände"
                                artikelnummer={info.row.original.artikelnummer}
                                onRefreshed={handleArticleRefreshed}
                            />
                        </span>
                    );
                },
                meta: {
                    width: 150,
                    pickerLabel: "Sollstatus B2B-Shop",
                    filterVariant: "select",
                    selectOptions: [
                        {value: "", label: "Alle"},
                        {value: "in", label: "Auf Lager"},
                        {value: "out", label: "In Zulauf"},
                        {value: "no_stock", label: "ohne Bestand"},
                        {value: "__none__", label: "– (nicht überwacht)"},
                    ],
                    help: "Wie der B2B-Shop laut Automatik stehen müsste: Liegen in SNV Weilheim weniger als 24 Stück, lautet der Sollstatus „In Zulauf\", sonst „Auf Lager\". Diese Spalte ist also der Soll-, die Spalte „B2B-Shop-Lagerstatus\" der Ist-Zustand. Weichen beide voneinander ab, steht der stündliche Abgleich noch aus – bleibt die Abweichung länger bestehen, stimmt etwas nicht. Gefüllt nur für überwachte Artikel; „–\" = nicht überwacht oder kein Bestand in Weilheim hinterlegt.",
                },
            }),
            columnHelper.accessor("reserved_quantity", {
                id: "reserved_quantity",
                header: "Offen, nicht reserviert",
                cell: (info) => {
                    const value = info.getValue();
                    const row = info.row.original;
                    const stand = row.reserved_synced_at
                        ? `Stand der offenen Mengen: ${formatTimestamp(row.reserved_synced_at)} (stündlicher Abgleich mit Weclapp).`
                        : "Die offenen Mengen wurden noch nicht abgeglichen.";
                    return <span className="d-inline-flex align-items-center justify-content-end gap-1 w-100 text-muted">
                        <BestandTooltip
                            werte={{offenNichtReserviert: value === null || value === undefined ? 0 : value}}
                            kopf={row.offene_menge !== null && row.offene_menge !== undefined
                                ? `Offene Menge aller offenen Aufträge: ${formatQuantity(row.offene_menge)}, davon reserviert: ${formatQuantity(row.hart_reserviert_gesamt ?? 0)}.`
                                : undefined}
                            quelle={stand}
                        >
                            <span tabIndex={0}>{formatQuantity(value)}</span>
                        </BestandTooltip>
                        {Number(row.offene_menge) > 0 && (
                            <HelpTooltip
                                id={`reservation-details-${row.artikelnummer}`}
                                placement="bottom"
                                size={0.85}
                                iconPath={mdiInformationOutline}
                                ariaLabel={`Offene Aufträge für ${row.artikelnummer} anzeigen`}
                                className="reservation-info-trigger"
                                onClick={() => setReservationArtikelnummer(row.artikelnummer)}
                            >
                                Offene Aufträge anzeigen
                            </HelpTooltip>
                        )}
                    </span>;
                },
                meta: {
                    width: 140,
                    pickerLabel: "Offen, nicht reserviert",
                    filterVariant: "number",
                    help: "Grau, nur zur Information: Wie viele Stück dieses Artikels verkauft, aber noch nicht ausgeliefert sind und noch nicht reserviert wurden. Gezählt werden die Aufträge „in Erfassung“ und mit gedruckter Auftragsbestätigung, je Position bestellte minus gelieferte Menge, abzüglich der schon reservierten Menge. Diese Zahl wird NICHT vom verfügbaren Bestand abgezogen. Weclapp führt den Wert nicht – er wird hier berechnet; das Info-Symbol neben der Zahl zeigt die einzelnen Aufträge dahinter.",
                },
            }),
            ...(priceColumns || []).map((price) => columnHelper.accessor(price.id, {
                id: price.id,
                header: price.label,
                cell: (info) => {
                    const row = info.row.original;
                    const pairs = {
                        price_weclapp_gross1: [row.price_weclapp_gross1, row.price_b2c_price], price_b2c_price: [row.price_weclapp_gross1, row.price_b2c_price],
                        price_weclapp_net4: [row.price_weclapp_net4, row.price_b2b_price], price_b2b_price: [row.price_weclapp_net4, row.price_b2b_price],
                        price_weclapp_net3: [row.price_weclapp_net3, row.price_b2b_molotow_palettprice2], price_b2b_molotow_palettprice2: [row.price_weclapp_net3, row.price_b2b_molotow_palettprice2],
                    };
                    const b2cIds = ["price_weclapp_gross1", "price_b2c_price"];
                    const b2bIds = ["price_weclapp_net4", "price_b2b_price", "price_weclapp_net3", "price_b2b_molotow_palettprice2"];
                    const pair = pairs[price.id];
                    const applies = (priceComparison === "b2c" && b2cIds.includes(price.id)) || (priceComparison === "b2b" && b2bIds.includes(price.id));
                    const [left, right] = pair || [];
                    const bothEmpty = left == null && right == null;
                    const paletteEmpty = priceComparison === "b2b" && ["price_weclapp_net3", "price_b2b_molotow_palettprice2"].includes(price.id) && right == null;
                    const matching = left != null && right != null && Number(left).toFixed(4) === Number(right).toFixed(4);
                    const css = applies && !bothEmpty && !paletteEmpty ? (matching ? "bg-success-subtle" : "bg-danger-subtle") : "";
                    return <span className={`d-block px-1 rounded ${css}`}>{formatPrice(info.getValue(), row[`${price.id}_currency`])}</span>;
                },
                meta: {width: 180, pickerLabel: price.label, filterVariant: "number", help: `${price.label} (${price.source === "weclapp" ? "Weclapp-Verkaufspreis" : price.source === "b2b" ? "B2B-Shop" : "B2C-Shop"}). Fehlende Preise werden als Strich dargestellt.`},
            })),
            columnHelper.accessor("synced_at", {
                id: "synced_at",
                header: "Stand",
                cell: (info) => formatTimestamp(info.getValue()),
                meta: {
                    width: 155,
                    pickerLabel: "Letzter Bestandsabgleich",
                    help: "Wann die Bestandszahlen dieser Zeile zuletzt aus Weclapp geholt wurden. Alles links davon ist also der Stand von diesem Zeitpunkt, nicht zwingend der Stand von jetzt.",
                },
            }),
        ],
        [warengruppen, warehouseLabels, handleArticleRefreshed, priceColumns, priceComparison]
    );

    const columnPickerGroups = useMemo(() => [
        {
            label: "Artikel",
            columnIds: ["warengruppe"],
        },
        {
            label: "Lager & offene Mengen",
            columnIds: ["snv", "snv_reserved", "snv_available", "snv_zero_since", "lahr", "lahr_reserved", "lahr_available", "lahr_zero_since", "ueberwacht", "reserved_quantity", "synced_at"],
        },
        {
            label: "B2C-Shop",
            columnIds: [
                "b2c_active",
                "b2c_status",
                ...priceColumns.filter((price) => price.source === "b2c").map((price) => price.id),
            ],
        },
        {
            label: "B2B-Shop",
            columnIds: [
                "b2b_active",
                "b2b_status",
                "soll_status",
                ...priceColumns.filter((price) => price.source === "b2b").map((price) => price.id),
            ],
        },
        {
            label: "Weclapp-Preise",
            columnIds: priceColumns.filter((price) => price.source === "weclapp").map((price) => price.id),
        },
    ], [priceColumns]);

    const allColumnIds = useMemo(() => columns.map((column) => column.id), [columns]);
    const expandColumns = useCallback((savedColumns) => {
        const saved = Array.isArray(savedColumns) ? savedColumns : [];
        if (saved.includes("__all__")) return allColumnIds;
        const prices = priceColumns.map((price) => price.id);
        const sourcePrices = (source) => priceColumns.filter((price) => price.source === source).map((price) => price.id);
        return [...new Set(saved.flatMap((column) => {
            if (column === "__all_prices__") return prices;
            if (column === "__weclapp_prices__") return sourcePrices("weclapp");
            if (column === "__b2b_prices__") return sourcePrices("b2b");
            if (column === "__b2c_prices__") return sourcePrices("b2c");
            return [column];
        }))].filter((column) => allColumnIds.includes(column));
    }, [allColumnIds, priceColumns]);

    const applyView = useCallback((view, {updateUrl = true, preserveUrlFilters = false} = {}) => {
        if (!view) return;
        const filters = view.filters || {};
        const urlFilters = preserveUrlFilters ? parseInitialFilters() : [];
        // Ein expliziter URL-Filter gewinnt beim Einstieg über einen Link
        // gegenüber einem gleichnamigen Filter aus der gespeicherten Ansicht.
        const viewFilters = (filters.columnFilters || []).filter((filter) => !urlFilters.some((urlFilter) => urlFilter.id === filter.id));
        gridRef.current?.applyState({
            columns: expandColumns(view.columns),
            // Auch alte Ansichten verwenden Platzhalter wie
            // `__all_prices__`. Die Reihenfolge muss sie genau wie die
            // Sichtbarkeit expandieren, sonst wären Preis-Spalten nach einem
            // Reload willkürlich ans Tabellenende gerutscht.
            columnOrder: expandColumns(filters.columnOrder || view.columns),
            filters: [...viewFilters, ...urlFilters],
            sorting: filters.sorting || [{id: "artikelnummer", desc: false}],
            // Alte Ansichten besitzen noch keinen Zusatzfilter-Snapshot.
            // Der sichtbare Standard bleibt trotzdem der globale Default,
            // statt einen Wert aus der zuvor geöffneten Ansicht mitzunehmen.
            additionalFilters: {...DEFAULT_ADDITIONAL_FILTERS, ...(filters.additionalFilters || {})},
            search: filters.search || "",
            advancedSearch: filters.advancedSearch || {version: 1, article_numbers: "", groups: []},
        });
        setActiveViewId(view.id);
        if (updateUrl && typeof window !== "undefined") {
            const url = new URL(window.location.href);
            // Ein bewusster Ansichtswechsel ist ein neuer Zustand. Die beiden
            // Link-Vorfilter gelten nur beim Einstieg und dürfen beim Reload
            // nicht wieder über die gerade gewählte Ansicht gelegt werden.
            ["ueberwacht", "soll_status"].forEach((key) => url.searchParams.delete(key));
            url.searchParams.set("view", view.slug);
            window.history.replaceState({}, "", url);
        }
    }, [expandColumns]);

    useEffect(() => {
        const initial = views.find((view) => view.id === activeViewId);
        if (initial && columns.length) applyView(initial, {updateUrl: false, preserveUrlFilters: true});
        // Nur beim Eintreffen der vorgegebenen Ansichten anwenden; spätere
        // Benutzeränderungen bleiben ausdrücklich persönlich.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns.length]);

    const onGridStateChange = useCallback((next) => {
        setGridState((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    }, []);

    const activeView = views.find((view) => view.id === activeViewId);
    const priceComparisonMode = activeView?.slug === "b2c-preise"
        ? "b2c"
        : activeView?.slug === "b2b-preise"
            ? "b2b"
            : null;
    const gridDiffersFromActiveView = useMemo(() => {
        if (!activeView || !gridState) return false;
        const defaults = activeView.filters || {};
        const equal = (left, right) => JSON.stringify(left || []) === JSON.stringify(right || []);
        return !equal(gridState.filters, defaults.columnFilters)
            || !equal(gridState.sorting, defaults.sorting || [{id: "artikelnummer", desc: false}])
            || (gridState.search || "") !== (defaults.search || "")
            || JSON.stringify(gridState.advancedSearch || {}) !== JSON.stringify(defaults.advancedSearch || {version: 1, article_numbers: "", groups: []})
            || JSON.stringify({...DEFAULT_ADDITIONAL_FILTERS, ...(gridState.additionalFilters || {})}) !== JSON.stringify({...DEFAULT_ADDITIONAL_FILTERS, ...(defaults.additionalFilters || {})});
    }, [activeView, gridState]);

    const togglePriceComparison = () => {
        const state = gridRef.current?.getState();
        if (priceComparison) {
            setPriceComparison(null);
            gridRef.current?.applyState({
                ...state,
                filters: (state?.filters || []).filter((filter) => filter.id !== "price_comparison"),
            });
            return;
        }
        setPriceComparison(priceComparisonMode);
    };

    const toggleOnlyPriceDifferences = (event) => {
        const state = gridRef.current?.getState();
        gridRef.current?.applyState({
            ...state,
            filters: [
                ...(state?.filters || []).filter((filter) => filter.id !== "price_comparison"),
                ...(event.target.checked ? [{id: "price_comparison", value: priceComparison}] : []),
            ],
        });
    };

    const saveView = async ({create = false} = {}) => {
        const target = create ? null : activeView;
        const name = target ? target.name : viewName.trim();
        if (!name || !gridState) return;
        const payload = {name, columns: gridState.columns, filters: {columnFilters: gridState.filters, columnOrder: gridState.columnOrder, sorting: gridState.sorting, additionalFilters: gridState.additionalFilters, search: gridState.search, advancedSearch: gridState.advancedSearch}};
        const response = target ? await axios.put(`/stock/overview/views/${target.id}`, payload) : await axios.post("/stock/overview/views", payload);
        const saved = response.data.view;
        setViews((current) => target ? current.map((item) => item.id === saved.id ? saved : item) : [...current, saved]);
        if (create) setDraftViewIds((current) => [...current, saved.id]);
        applyView(saved);
        setViewName("");
    };

    const startEditing = () => {
        setDraftViewIds(views.map((view) => view.id));
        setEditMode(true);
    };

    const moveView = (sourceId, targetId, afterTarget) => {
        setDraftViewIds((current) => {
            if (sourceId === targetId || !current.includes(sourceId) || !current.includes(targetId)) return current;
            const next = current.filter((id) => id !== sourceId);
            const targetIndex = next.indexOf(targetId);
            next.splice(targetIndex + (afterTarget ? 1 : 0), 0, sourceId);
            return next;
        });
    };

    const saveEditing = async () => {
        const response = await axios.put("/stock/overview/views-reorder", {ids: draftViewIds});
        setViews(response.data.views);
        setEditMode(false);
    };

    const cancelEditing = () => {
        setEditMode(false);
        // Header-Reihenfolge, Filter und Sichtbarkeit können während der
        // Bearbeitung bereits lokal geändert sein. Abbrechen bedeutet daher
        // nicht nur „Button-Entwurf verwerfen“, sondern den gespeicherten
        // Zustand der aktiven Ansicht vollständig wiederherstellen.
        applyView(activeView, {updateUrl: false});
    };

    const displayedViews = editMode
        ? draftViewIds.map((id) => views.find((view) => view.id === id)).filter(Boolean)
        : views;

    const additionalFilters = useMemo(
        () => [
            {
                key: "only_shop_articles",
                label: "Nur Artikel zeigen, die in mindestens einem Onlineshop verkauft werden",
                defaultValue: true,
                help: "Blendet Artikel aus, die es weder im B2C- noch im B2B-Shop gibt (z. B. reine Weclapp-Stammdaten ohne Shop-Anbindung). Ausschalten zeigt wirklich alle Artikel dieser Lagerübersicht.",
            },
            {key: "shop_scope", label: "Shop", type: "select", options: [{value: "", label: "Alle Shop-Artikel"}, {value: "b2c", label: "Nur B2C-Artikel anzeigen"}, {value: "b2b", label: "Nur B2B-Artikel anzeigen"}], defaultValue: "", help: "Begrenzt die Übersicht auf Artikel, die tatsächlich im ausgewählten Shop existieren."},
        ],
        []
    );

    return (
        <Container fluid className="pt-2">
            {stockWarning?.stale && (
                <Alert variant="warning">
                    <strong>Bestandsdaten möglicherweise veraltet</strong> – letzter erfolgreicher Abgleich: {formatTimestamp(stockWarning.last_successful_at)}.
                    <Button size="sm" variant="outline-dark" className="ms-2" disabled={refreshingStock} onClick={refreshStockData}>{refreshingStock ? "Aktualisiere…" : "Jetzt aktualisieren"}</Button>
                    {stockRefreshError && <span className="ms-2 text-danger">{stockRefreshError}</span>}
                </Alert>
            )}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <div className="d-inline-flex align-items-stretch p-1 rounded-pill bg-body-secondary" role="tablist" aria-label="Lageransichten">
                    {displayedViews.map((view) => <Button key={view.id} size="sm" variant="link" role="tab" aria-selected={view.id === activeViewId} draggable={editMode} onDragStart={(event) => { viewWasDraggedRef.current = true; event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("application/x-stock-view", String(view.id)); }} onDragEnd={() => window.setTimeout(() => { viewWasDraggedRef.current = false; }, 0)} onDragOver={(event) => editMode && event.preventDefault()} onDrop={(event) => { if (!editMode) return; event.preventDefault(); const bounds = event.currentTarget.getBoundingClientRect(); moveView(Number(event.dataTransfer.getData("application/x-stock-view")), view.id, event.clientX > bounds.left + (bounds.width / 2)); }} className={`rounded-pill border-0 text-decoration-none ${view.id === activeViewId ? "bg-white text-body shadow-sm fw-semibold" : "text-body"} ${editMode ? "cursor-grab" : ""}`} style={{padding: ".3rem 1.2rem"}} onClick={() => { if (!viewWasDraggedRef.current) { setPriceComparison(null); applyView(view); } }}>{view.name}</Button>)}
                </div>
                <HelpTooltip placement="right" size={0.85}>Bestands-, Preis- und Zustandsinformationen aus Weclapp und den Shops.</HelpTooltip>
                {isAdmin && <>
                    {!editMode && <Button size="sm" variant="outline-secondary" onClick={startEditing}>Bearbeitungsmodus</Button>}
                    {editMode && <>
                        <span className="small text-muted">Ansichten und Spaltenüberschriften ziehen, anschließend sichern.</span>
                        <Button size="sm" variant="primary" disabled={!activeView || !gridState} onClick={() => saveView()}>Aktuelle Ansicht sichern</Button>
                        <Form.Control size="sm" value={viewName} onChange={(event) => setViewName(event.target.value)} placeholder="Neue Ansicht" style={{width: 160}} />
                        <Button size="sm" variant="success" onClick={saveEditing}>Reihenfolge speichern</Button>
                        <Button size="sm" variant="outline-secondary" onClick={cancelEditing}>Abbrechen</Button>
                    </>}
                </>}
                {/* Rechts abgesetzt, damit der Knopf nicht als weitere Ansicht
                    missverstanden wird. */}
                <div className="ms-auto">
                    <DataFreshnessButton />
                </div>
            </div>

            <DataGrid
                ref={gridRef}
                columns={columns}
                getRowId={(row) => row.artikelnummer}
                initialColumnFilters={initialColumnFilters}
                initialSorting={[{id: "artikelnummer", desc: false}]}
                lockedColumnIds={["hex_color", "artikelnummer", "bezeichnung"]}
                columnPickerGroups={columnPickerGroups}
                columnReorderMode={isAdmin && editMode}
                onStateChange={onGridStateChange}
                groupingColumnId="warengruppe"
                emptyLabel="Keine Artikel gefunden."
                dataUrl="/stock/overview/data"
                groupsUrl="/stock/overview/groups"
                exportUrl="/stock/overview/export"
                additionalFilters={additionalFilters}
                advancedSearchConfig={{fields: advancedSearchFields, savedSearches, saveUrl: "/stock/overview/saved-searches"}}
                defaultPageSize={100}
                compactHeader
                toolbarActions={priceComparisonMode && <>
                    <div className="d-inline-flex align-items-center gap-1">
                        <Form.Check
                            type="switch"
                            id="stock-price-comparison-switch"
                            className="mb-0"
                            label="Preisvergleich starten"
                            checked={!!priceComparison}
                            onChange={togglePriceComparison}
                        />
                        <HelpTooltip id="stock-price-comparison-help" placement="bottom">
                            {priceComparisonMode === "b2c"
                                ? "Vergleicht PG1 aus Weclapp mit dem B2C-Shoppreis."
                                : "Vergleicht PG2 mit dem B2B-Shoppreis sowie PG3 mit dem Palettenpreis, falls dieser im Shop vorhanden ist."}
                            {" "}Grün bedeutet gleich, rot abweichend; „Nur Abweichungen“ zeigt ausschließlich abweichende Artikel.
                        </HelpTooltip>
                    </div>
                    {priceComparison && (
                        <Form.Check
                            type="switch"
                            id="stock-price-comparison-only-differences"
                            className="mb-0"
                            label="Nur Abweichungen"
                            checked={(gridState?.filters || []).some((filter) => filter.id === "price_comparison")}
                            onChange={toggleOnlyPriceDifferences}
                        />
                    )}
                </>}
                resetAction={gridDiffersFromActiveView && <button type="button" className="btn btn-link btn-sm p-0" onClick={() => { setPriceComparison(null); applyView(activeView, {updateUrl: false}); }}>Filter und Sortierung zurücksetzen</button>}
            />

            <ArticleHistoryModal
                show={!!historyArtikelnummer}
                artikelnummer={historyArtikelnummer}
                onHide={() => setHistoryArtikelnummer(null)}
            />
            <ReservationModal show={!!reservationArtikelnummer} artikelnummer={reservationArtikelnummer} onHide={() => setReservationArtikelnummer(null)} weclappBaseUrl={weclappBaseUrl} />
        </Container>
    );
}
